import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {
    Alert, Badge, Box, Button, Chip, CircularProgress, DialogContent, DialogTitle,
    IconButton, List, Tab, Tabs, Tooltip, Typography
} from '@mui/material';
import {
    CheckRounded, CheckCircleOutlineRounded, CloseRounded, ErrorOutlineRounded,
    InfoOutlined, NotificationsNoneRounded, RefreshRounded, WarningAmberRounded
} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import moment from 'moment';
import {notificationServices} from '../../service/notificationService';
import {SettingsDialog} from '../settings/SettingsDialog';
import {getSettingsColors, settingsTabButtonStyles} from '../settings/SettingsDesign';
import {getNotificationTarget} from './notificationLinks';

const severityIcons = {
    info: InfoOutlined,
    success: CheckCircleOutlineRounded,
    warning: WarningAmberRounded,
    error: ErrorOutlineRounded
};

const errorMessage = (error, fallback) => error?.response?.data?.error || fallback;

export default function Notifications() {
    const history = useHistory();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const [revision, setRevision] = useState(0);
    const workspaces = useSelector(state => state.workspace?.userWorkspaces);

    useEffect(() => {
        let active = true;
        let pending = false;

        async function checkUnread() {
            if (document.visibilityState === 'hidden' || pending) return;
            pending = true;
            try {
                // The API has no total count; one unread record is enough for the dot.
                const response = await notificationServices.read({unreadOnly: true, limit: 1});
                if (active) setHasUnread(response.notifications.length > 0);
            } catch {
                // Background failures are surfaced by the inbox when it is opened.
            } finally {
                pending = false;
            }
        }

        checkUnread();
        if (!open) {
            const interval = window.setInterval(checkUnread, 60000);
            window.addEventListener('focus', checkUnread);
            document.addEventListener('visibilitychange', checkUnread);
            return () => {
                active = false;
                window.clearInterval(interval);
                window.removeEventListener('focus', checkUnread);
                document.removeEventListener('visibilitychange', checkUnread);
            };
        }
        return () => {
            active = false;
        };
    }, [open, revision]);

    return <>
        <Tooltip title={hasUnread ? 'Notifications · Unread messages' : 'Notifications'}>
            <IconButton color="inherit" aria-label={hasUnread ? 'Notifications, unread messages' : 'Notifications'}
                        aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
                <Badge color="error" variant="dot" invisible={!hasUnread}>
                    <NotificationsNoneRounded/>
                </Badge>
            </IconButton>
        </Tooltip>
        {open && <NotificationsDialog workspaces={workspaces} onClose={() => setOpen(false)}
                                      onRead={() => setRevision(value => value + 1)}
                                      onOpenNotification={notification => {
                                          const target = getNotificationTarget(notification, location);
                                          if (!target) return;
                                          if (target.external) {
                                              window.open(target.location, '_blank', 'noopener,noreferrer');
                                          } else {
                                              setOpen(false);
                                              history.push(target.location);
                                          }
                                      }}/>}
    </>;
}

export function NotificationsDialog({workspaces, onClose, onRead, onOpenNotification}) {
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [revision, setRevision] = useState(0);
    const [page, setPage] = useState({notifications: [], hasMore: false});
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [readingId, setReadingId] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [readError, setReadError] = useState('');
    const requestVersion = useRef(0);

    useEffect(() => {
        const version = ++requestVersion.current;
        setLoading(true);
        setLoadingMore(false);
        setLoadError('');
        setReadError('');
        setPage({notifications: [], hasMore: false});
        notificationServices.read({unreadOnly}).then(response => {
            if (version === requestVersion.current) setPage(response);
        }).catch(error => {
            if (version === requestVersion.current) setLoadError(errorMessage(error, 'Unable to load notifications. Please try again.'));
        }).finally(() => {
            if (version === requestVersion.current) setLoading(false);
        });
        // Ignore responses from a closed dialog, previous filter, or refresh.
        return () => {
            requestVersion.current = version + 1;
        };
    }, [unreadOnly, revision]);

    async function loadMore() {
        const version = requestVersion.current;
        setLoadingMore(true);
        setLoadError('');
        try {
            const response = await notificationServices.read({unreadOnly, cursor: page.nextCursor});
            if (version === requestVersion.current) {
                setPage(previous => {
                    const ids = new Set(previous.notifications.map(item => item.id));
                    return {...response, notifications: [...previous.notifications, ...response.notifications.filter(item => !ids.has(item.id))]};
                });
            }
        } catch (error) {
            if (version === requestVersion.current) setLoadError(errorMessage(error, 'Unable to load more notifications. Please try again.'));
        } finally {
            if (version === requestVersion.current) setLoadingMore(false);
        }
    }

    async function markRead(notification) {
        const version = requestVersion.current;
        setReadingId(notification.id);
        setReadError('');
        try {
            await notificationServices.markRead([notification.id]);
            if (version === requestVersion.current) {
                setPage(previous => ({
                    ...previous, notifications: unreadOnly
                        ? previous.notifications.filter(item => item.id !== notification.id)
                        : previous.notifications.map(item => item.id === notification.id ? {...item, isRead: true} : item)
                }));
                onRead();
            }
        } catch (error) {
            if (version === requestVersion.current) setReadError(errorMessage(error, 'Unable to mark this notification as read. Please try again.'));
        } finally {
            if (version === requestVersion.current) setReadingId(null);
        }
    }

    const busy = loading || loadingMore || readingId !== null;

    return <SettingsDialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="notifications-title"
                           aria-describedby="notifications-description"
                           sx={{'& .MuiDialog-paper': {borderRadius: '16px', maxHeight: 'calc(100% - 32px)', m: 2, width: 'calc(100% - 32px)'}}}>
        <DialogTitle id="notifications-heading" sx={{pr: 2}}>
            <Box component="span" sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                <Box component="span" id="notifications-title" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <NotificationsNoneRounded color="primary"/>Notifications
                </Box>
                <IconButton aria-label="Close notifications" onClick={onClose} size="small"><CloseRounded/></IconButton>
            </Box>
            <Typography component="span" id="notifications-description"
                        sx={{display: 'block', fontSize: 13, fontWeight: 400, color: 'text.secondary', mt: 0.75}}>
                Updates from all your workspaces, kept for 7 days.
            </Typography>
        </DialogTitle>
        <Box
            sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: {xs: 2, sm: 3}, pb: 1}}>
            <Tabs value={unreadOnly ? 'unread' : 'all'} onChange={(event, value) => setUnreadOnly(value === 'unread')}
                  aria-label="Filter notifications" sx={{
                minHeight: 0, p: 0.5, borderRadius: '10px', width: 'fit-content',
                bgcolor: theme => getSettingsColors(theme).inset,
                '& .MuiTabs-list': {gap: 0.5},
                '& .MuiTabs-indicator': {display: 'none'},
                '& .MuiTab-root': {
                    ...settingsTabButtonStyles, minWidth: 0, fontWeight: 600, color: 'text.secondary',
                    '&.Mui-selected': {bgcolor: theme => alpha(theme.palette.primary.main, 0.1), color: 'primary.main'}
                }
            }}>
                <Tab label="All" value="all" disabled={readingId !== null}/>
                <Tab label="Unread" value="unread" disabled={readingId !== null}/>
            </Tabs>
            <Tooltip title="Refresh notifications">
                <span><IconButton size="small" aria-label="Refresh notifications" disabled={busy}
                                  onClick={() => setRevision(value => value + 1)}><RefreshRounded
                    fontSize="small"/></IconButton></span>
            </Tooltip>
        </Box>
        <DialogContent sx={{minHeight: 220}} aria-busy={loading || loadingMore}>
            {readError && <Alert severity="error" sx={{mt: 2}} onClose={() => setReadError('')}>{readError}</Alert>}
            {loadError && <Alert severity="error" sx={{mt: 2}} action={<Button color="inherit" size="small"
                                                                               onClick={() => page.nextCursor ? loadMore() : setRevision(value => value + 1)}
                                                                               disabled={busy}>Retry</Button>}>
                {loadError}
            </Alert>}
            {loading ? <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress size={28} aria-label="Loading notifications"/>
            </Box> : <>
                {!loadError && page.notifications.length === 0 &&
                    <Box role="status" sx={{textAlign: 'center', py: 6, color: 'text.secondary'}}>
                        <NotificationsNoneRounded sx={{fontSize: 40, mb: 1, opacity: 0.6}}/>
                        <Typography sx={{fontSize: 15, fontWeight: 600, color: 'text.primary'}}>
                            {unreadOnly ? 'You’re all caught up' : 'No notifications yet'}
                        </Typography>
                        <Typography sx={{fontSize: 13, mt: 0.75}}>
                            {unreadOnly ? 'You have no unread notifications.' : 'New updates will appear here.'}
                        </Typography>
                    </Box>}
                <List aria-label="Notifications" disablePadding sx={{pt: 2, display: 'grid', gap: 1.5}}>
                    {page.notifications.map(notification => {
                        const severity = severityIcons[notification.severity] ? notification.severity : 'info';
                        const SeverityIcon = severityIcons[severity];
                        const workspaceName = workspaces?.find(item => String(item.workspaceId) === String(notification.workspaceId))?.name;
                        const createdAt = moment(notification.createdAt);
                        const target = getNotificationTarget(notification);
                        const canOpen = Boolean(onOpenNotification && target);
                        return <Box component="li" key={notification.id} onClick={canOpen ? () => onOpenNotification(notification) : undefined} sx={{
                            p: 2, borderRadius: '12px', border: '1px solid',
                            borderColor: 'divider', bgcolor: theme => notification.isRead ? 'transparent' : alpha(theme.palette.primary.main, 0.06),
                            cursor: canOpen ? 'pointer' : undefined,
                            '&:hover': canOpen ? {borderColor: 'primary.main'} : undefined
                        }}>
                            <Box sx={{display: 'flex', gap: 1.25, alignItems: 'flex-start'}}>
                                <SeverityIcon sx={{color: `${severity}.main`, mt: 0.25, fontSize: 21}}
                                              titleAccess={severity}/>
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <Typography component={canOpen ? 'button' : 'h3'} type={canOpen ? 'button' : undefined}
                                                    aria-label={canOpen ? `${target.label}: ${notification.title}` : undefined}
                                                    sx={{fontSize: 14, fontWeight: notification.isRead ? 500 : 700, overflowWrap: 'anywhere',
                                                        p: 0, border: 0, bgcolor: 'transparent', color: 'text.primary', fontFamily: 'inherit', textAlign: 'left', cursor: 'inherit'}}>
                                            {notification.title}
                                        </Typography>
                                        {!notification.isRead && <Box component="span" aria-label="Unread"
                                                                      sx={{width: 7, height: 7, flexShrink: 0, borderRadius: '50%', bgcolor: 'primary.main'}}/>}
                                    </Box>
                                    <Typography
                                        sx={{fontSize: 13, color: 'text.secondary', mt: 0.75, lineHeight: 1.65, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
                                        {notification.message}
                                    </Typography>
                                    <Box
                                        sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1.5}}>
                                        <Chip size="small"
                                              label={workspaceName || `Workspace ${notification.workspaceId}`}
                                              sx={{fontSize: 11, height: 23, maxWidth: '100%'}}/>
                                        <Tooltip title={createdAt.format('lll')}>
                                            <Typography component="time" dateTime={notification.createdAt}
                                                        sx={{fontSize: 11, color: 'text.secondary'}}>{createdAt.fromNow()}</Typography>
                                        </Tooltip>
                                        <Box sx={{flex: 1}}/>
                                        {notification.isRead ? <Typography
                                                sx={{fontSize: 12, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                <CheckRounded sx={{fontSize: 16}}/>Read
                                            </Typography> :
                                            <Button size="small" disabled={busy} onClick={event => {event.stopPropagation(); markRead(notification);}}
                                                    aria-label={`Mark ${notification.title} as read`}
                                                    startIcon={readingId === notification.id ?
                                                        <CircularProgress size={14} color="inherit"/> : <CheckRounded/>}
                                                    sx={{fontSize: 12, textTransform: 'none'}}>Mark as read</Button>}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>;
                    })}
                </List>
                {page.hasMore && page.nextCursor && <Box sx={{display: 'flex', justifyContent: 'center', mt: 2}}>
                    <Button onClick={loadMore} disabled={busy}
                            startIcon={loadingMore ? <CircularProgress size={16} color="inherit"/> : undefined}>
                        {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                </Box>}
            </>}
        </DialogContent>
    </SettingsDialog>;
}
